import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const BREAK_TYPES = [
  { key: 'lunch',   label: 'Lunch',     icon: '🍽' },
  { key: 'tea',     label: 'Tea break', icon: '☕' },
  { key: 'casual',  label: 'Casual',    icon: '💬' },
  { key: 'other',   label: 'Other',     icon: '⏸' },
];

function fmtDuration(ms) {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function fmtTimer(ms) {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function AttendanceFAB() {
  const { user, isOwner } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const panelRef = useRef(null);

  // Tick every second for live timers
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const { data: todayData } = useQuery({
    queryKey: ['fab-today'],
    queryFn: () => api.get('/attendance-sessions/today').then(r => r.data),
    refetchInterval: 30000,
    enabled: !!user,
  });

  const { data: teamData } = useQuery({
    queryKey: ['fab-team'],
    queryFn: () => api.get('/attendance-sessions/team-today').then(r => r.data),
    refetchInterval: 60000,
    enabled: !!user && isOwner,
  });

  // Heartbeat — send every 2 min while clocked in and not clocked out
  const heartbeatRef = useRef(null);
  useEffect(() => {
    const session = todayData?.session;
    if (session && session.clock_in && !session.clock_out) {
      // Send immediately on first mount/change, then every 2 min
      api.post('/attendance-sessions/heartbeat').catch(() => {});
      heartbeatRef.current = setInterval(() => {
        api.post('/attendance-sessions/heartbeat').catch(() => {});
      }, 2 * 60 * 1000);
    }
    return () => clearInterval(heartbeatRef.current);
  }, [todayData?.session?.id, !!todayData?.session?.clock_out]);

  const invalidate = useCallback(() => {
    qc.invalidateQueries(['fab-today']);
    qc.invalidateQueries(['team-today']);
    qc.invalidateQueries(['fab-team']);
  }, [qc]);

  const clockInMutation = useMutation({
    mutationFn: () => api.post('/attendance-sessions/clock-in'),
    onSuccess: () => { invalidate(); toast.success('Clocked in'); },
    onError: e => toast.error(e?.response?.data?.error || 'Failed to clock in'),
  });

  const clockOutMutation = useMutation({
    mutationFn: () => api.post('/attendance-sessions/clock-out'),
    onSuccess: () => { invalidate(); toast.success('Clocked out'); },
    onError: e => toast.error(e?.response?.data?.error || 'Failed to clock out'),
  });

  const resumeMutation = useMutation({
    mutationFn: () => api.post('/attendance-sessions/resume'),
    onSuccess: () => { invalidate(); toast.success('Welcome back — you are clocked in again'); },
    onError: e => toast.error(e?.response?.data?.error || 'Failed to resume'),
  });

  const breakStartMutation = useMutation({
    mutationFn: (break_type) => api.post('/attendance-sessions/break/start', { break_type }),
    onSuccess: (_, type) => {
      invalidate();
      const label = BREAK_TYPES.find(b => b.key === type)?.label || 'Break';
      toast.success(`${label} started`);
    },
    onError: e => toast.error(e?.response?.data?.error || 'Failed'),
  });

  const breakEndMutation = useMutation({
    mutationFn: () => api.post('/attendance-sessions/break/end'),
    onSuccess: () => { invalidate(); toast.success('Welcome back!'); },
    onError: e => toast.error(e?.response?.data?.error || 'Failed'),
  });

  if (!user) return null;

  const session = todayData?.session;
  const activeBreak = todayData?.activeBreak;
  const totalBreakMinutes = todayData?.totalBreakMinutes || 0;
  const dayOff = todayData?.dayOff;

  // Derive state
  const isWorking = session && !session.clock_out && !activeBreak;
  const isOnBreak = session && !session.clock_out && !!activeBreak;
  const isDone = session && !!session.clock_out;

  // Pulse if past work start (10:00) and not clocked in
  const hour = new Date().getHours();
  const shouldPulse = !session && !dayOff && hour >= 10 && hour < 20;

  // Break elapsed
  const breakMs = activeBreak ? now - new Date(activeBreak.started_at).getTime() : 0;
  const breakLabel = BREAK_TYPES.find(b => b.key === activeBreak?.break_type)?.label || 'Break';
  const breakIcon = BREAK_TYPES.find(b => b.key === activeBreak?.break_type)?.icon || '⏸';

  // Worked time = elapsed - completed breaks - current active break
  const workedMs = session && session.clock_in
    ? Math.max(0,
        (session.clock_out ? new Date(session.clock_out) : now)
        - new Date(session.clock_in)
        - (totalBreakMinutes * 60000)
        - breakMs
      )
    : 0;

  // FAB label
  let fabLabel, fabStyle;
  if (dayOff) {
    fabLabel = '🏖 Day off';
    fabStyle = { background: '#6B7280' };
  } else if (!session) {
    fabLabel = '⏱ Clock in';
    fabStyle = { background: '#1D4ED8' };
  } else if (isOnBreak) {
    fabLabel = `${breakIcon} ${fmtDuration(breakMs)}`;
    fabStyle = { background: '#B45309' };
  } else if (isWorking) {
    fabLabel = `● ${fmtTimer(workedMs)}`;
    fabStyle = { background: '#15803D' };
  } else {
    fabLabel = `✓ Done · ${fmtDuration(workedMs)}`;
    fabStyle = { background: '#4B5563' };
  }

  // Minute-ago labels for missed clock-in
  const missedMinutes = !session && !dayOff && todayData?.schedule
    ? (() => {
        const [sh, sm] = (todayData.schedule.weekday_start || '10:00').split(':').map(Number);
        const start = new Date(); start.setHours(sh, sm, 0, 0);
        return Math.max(0, Math.floor((now - start) / 60000));
      })()
    : 0;

  // Team strip for owner
  const team = (teamData?.team || []).filter(m => m.full_name);
  const inCount = team.filter(m => m.status === 'in' || m.status === 'late').length;
  const breakCount = team.filter(m => m.status === 'break').length;
  const doneCount = team.filter(m => m.status === 'done').length;
  const notInCount = team.filter(m => m.status === 'not-in').length;
  const offlineCount = team.filter(m => m.status === 'offline').length;

  const isPending = clockInMutation.isPending || clockOutMutation.isPending ||
    breakStartMutation.isPending || breakEndMutation.isPending || resumeMutation.isPending;

  const location = useLocation();
  const onInvoicePage = /\/(invoices|quotations)(\/|$)/.test(location.pathname);

  return (
    <div ref={panelRef} style={{ position: 'fixed', bottom: 24, ...(onInvoicePage ? { left: 24 } : { right: 24 }), zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: onInvoicePage ? 'flex-start' : 'flex-end', gap: 10 }}>

      {/* Panel */}
      {open && (
        <div style={{
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 16,
          width: 280,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          animation: 'fabIn .15s ease-out',
        }}>

          {/* Header */}
          <div style={{ padding: '13px 15px 11px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
            {!session && !dayOff && (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1, #111)' }}>
                  {user.name?.split(' ')[0]}
                </div>
                {missedMinutes > 5 ? (
                  <div style={{ marginTop: 6, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#DC2626', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                    ⚠ Work started {fmtDuration(missedMinutes * 60000)} ago
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text2, #6b7280)', marginTop: 2 }}>
                    Not clocked in yet
                  </div>
                )}
              </>
            )}

            {dayOff && (
              <div style={{ fontSize: 13, color: 'var(--text2, #6b7280)' }}>Today is a holiday 🏖</div>
            )}

            {session && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1, #111)' }}>
                    {user.name?.split(' ')[0]}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                    background: isOnBreak ? '#FEF3C7' : isDone ? '#F3F4F6' : '#DCFCE7',
                    color: isOnBreak ? '#B45309' : isDone ? '#4B5563' : '#15803D',
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: isOnBreak ? '#D97706' : isDone ? '#9CA3AF' : '#16A34A', display: 'inline-block' }}/>
                    {isOnBreak ? 'On break' : isDone ? 'Done' : 'Working'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                  <span style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px', color: 'var(--text1, #111)' }}>
                    {fmtTimer(Math.max(0, workedMs))}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text3, #9ca3af)' }}>worked today</span>
                </div>
              </>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>

            {/* Not clocked in */}
            {!session && !dayOff && (
              <button onClick={() => { clockInMutation.mutate(); setOpen(false); }} disabled={isPending}
                style={btnStyle('#15803D')}>
                ⏱ Clock in now
              </button>
            )}

            {/* Working — show break options */}
            {isWorking && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                  {BREAK_TYPES.map(b => (
                    <button key={b.key} onClick={() => breakStartMutation.mutate(b.key)} disabled={isPending}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--surface2, #f9fafb)', cursor: 'pointer', fontSize: 12, color: 'var(--text2, #374151)', fontWeight: 500, fontFamily: 'inherit' }}>
                      <span>{b.icon}</span>{b.label}
                    </button>
                  ))}
                </div>
                <div style={{ height: 1, background: 'var(--border, #e5e7eb)', margin: '2px 0' }}/>
                <button onClick={() => { clockOutMutation.mutate(); setOpen(false); }} disabled={isPending}
                  style={btnStyle('#DC2626')}>
                  ⏹ Clock out
                </button>
              </>
            )}

            {/* On break */}
            {isOnBreak && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#FEF3C7', borderRadius: 8, fontSize: 12, color: '#B45309', fontWeight: 500 }}>
                  <span>{breakIcon}</span>
                  <span>{breakLabel} · {fmtDuration(breakMs)}</span>
                </div>
                <button onClick={() => breakEndMutation.mutate()} disabled={isPending}
                  style={btnStyle('#15803D')}>
                  ▶ Resume work
                </button>
                <div style={{ height: 1, background: 'var(--border, #e5e7eb)', margin: '2px 0' }}/>
                <button onClick={() => { clockOutMutation.mutate(); setOpen(false); }} disabled={isPending}
                  style={{ ...btnStyle('#6B7280'), opacity: 0.65 }}>
                  ⏹ Clock out
                </button>
              </>
            )}

            {/* Done */}
            {isDone && (
              <div style={{ fontSize: 12, color: 'var(--text2, #6b7280)', textAlign: 'center', padding: '6px 0' }}>
                Clocked out at {fmtTime(session.clock_out)} · {fmtDuration(workedMs)} total
                {session.break_minutes > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text3, #9ca3af)', marginTop: 2 }}>
                    {fmtDuration(session.break_minutes * 60000)} break
                  </div>
                )}
                <button onClick={() => resumeMutation.mutate()} disabled={isPending}
                  style={{ ...btnStyle('#1D4ED8'), marginTop: 10, justifyContent: 'center' }}>
                  ↩ Clocked out by mistake? Resume work
                </button>
                <div style={{ fontSize: 10, color: 'var(--text3, #9ca3af)', marginTop: 4 }}>
                  The time since you clocked out is recorded as a break.
                </div>
              </div>
            )}
          </div>

          {/* Owner team strip */}
          {isOwner && team.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border, #e5e7eb)', padding: '9px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3, #9ca3af)' }}>Team today</span>
                <div style={{ display: 'flex', gap: 8, fontSize: 11, fontWeight: 600 }}>
                  {inCount > 0 && <span style={{ color: '#15803D' }}>{inCount} in</span>}
                  {breakCount > 0 && <span style={{ color: '#D97706' }}>{breakCount} break</span>}
                  {doneCount > 0 && <span style={{ color: '#6B7280' }}>{doneCount} done</span>}
                  {notInCount > 0 && <span style={{ color: '#9CA3AF' }}>{notInCount} out</span>}
                  {offlineCount > 0 && <span style={{ color: '#EF4444' }}>{offlineCount} offline</span>}
                </div>
              </div>
              {team.slice(0, 6).map(m => {
                const isIn = m.status === 'in' || m.status === 'late';
                const isBreak = m.status === 'break';
                const isDoneM = m.status === 'done';
                const isOut = m.status === 'not-in';
                const isOfflineM = m.status === 'offline';
                const initials = m.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                const firstName = m.full_name.split(' ')[0];
                const avatarBg = isIn ? '#DCFCE7' : isBreak ? '#FEF3C7' : isOfflineM ? '#FEE2E2' : '#F3F4F6';
                const avatarColor = isIn ? '#15803D' : isBreak ? '#B45309' : isOfflineM ? '#EF4444' : '#9CA3AF';
                const pillBg = isIn ? '#DCFCE7' : isBreak ? '#FEF3C7' : isOfflineM ? '#FEE2E2' : '#F3F4F6';
                const pillColor = isIn ? '#15803D' : isBreak ? '#B45309' : isOfflineM ? '#DC2626' : '#9CA3AF';
                const memberBreakLabel = BREAK_TYPES.find(b => b.key === m.activeBreak?.break_type)?.label || 'Break';
                const breakElapsed = m.activeBreak ? fmtDuration(now - new Date(m.activeBreak.started_at).getTime()) : '';
                const offlineSince = isOfflineM && m.session?.last_seen
                  ? fmtTime(m.session.last_seen) : null;
                const lastBreakLabel = m.lastBreak ? (BREAK_TYPES.find(b => b.key === m.lastBreak.break_type)?.label || 'Break') : null;
                const lastBreakDuration = m.lastBreak?.ended_at && m.lastBreak?.started_at
                  ? fmtDuration(new Date(m.lastBreak.ended_at) - new Date(m.lastBreak.started_at)) : null;
                const pillLabel = isIn ? (m.status === 'late' ? 'Late' : 'In') : isBreak ? memberBreakLabel : isDoneM ? 'Done' : isOfflineM ? 'Offline' : 'Out';
                const meta = isOut ? '—' : isBreak ? breakElapsed : isOfflineM ? (offlineSince ? `since ${offlineSince}` : '?') : m.session?.clock_in ? fmtDuration(now - new Date(m.session.clock_in).getTime()) : '—';
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 0' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, background: avatarBg, color: avatarColor }}>
                      {initials}
                    </div>
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text1, #111)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstName}</span>
                    <span style={{ fontSize: 11, color: isOfflineM ? '#DC2626' : 'var(--text2, #6b7280)' }}>{meta}</span>
                    {(isIn || isDoneM) && lastBreakLabel && lastBreakDuration && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: '#FEF3C7', color: '#B45309' }}>{lastBreakLabel} · {lastBreakDuration}</span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: pillBg, color: pillColor }}>{pillLabel}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FAB button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '11px 20px', borderRadius: 40, border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: 'inherit',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          position: 'relative',
          ...fabStyle,
        }}>
        {shouldPulse && (
          <>
            <span style={{
              position: 'absolute', inset: -4, borderRadius: 40,
              border: '2px solid #DC2626',
              animation: 'fabPulse 1.8s ease-out infinite',
              pointerEvents: 'none',
            }}/>
            <span style={{
              position: 'absolute', top: -5, right: -5,
              width: 16, height: 16, borderRadius: '50%',
              background: '#DC2626', border: '2px solid #fff',
              fontSize: 10, color: '#fff', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>!</span>
          </>
        )}
        {fabLabel}
      </button>

      <style>{`
        @keyframes fabPulse {
          0% { opacity: .7; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.2); }
        }
        @keyframes fabIn {
          from { opacity: 0; transform: translateY(8px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function btnStyle(bg) {
  return {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px', borderRadius: 10, border: 'none',
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
    fontFamily: 'inherit', width: '100%', textAlign: 'left',
    background: bg, color: '#fff',
  };
}
